import {
  Shield,
  CheckCircle,
  Users,
  ArrowRight,
  Cpu,
  Cloud,
  Target,
  Activity,
  Layers,
  Sparkles,
  FileText,
  Lock,
  Globe,
  Smartphone,
  BarChart3,
  Zap,
  Clock,
  Database,
  Settings,
  Bell,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import {
  ComplianceScoreWidget,
  ActiveWorkersWidget,
  OpenHazardsWidget,
} from '../components/dashboard-widgets';
import VHLogo from '../components/VHLogo';

export default function Features() {
  const [activeTab, setActiveTab] = useState('ai-intelligence');
  const [scrolled, setScrolled] = useState(false);
  const [animatedCounts, setAnimatedCounts] = useState({
    events: 0,
    accuracy: 0,
    response: 0,
  });

  // Handle scroll effect for header
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animate counter values when component mounts
  useEffect(() => {
    const timers = [
      setTimeout(() => {
        const interval = setInterval(() => {
          setAnimatedCounts((prev) => ({
            events: Math.min(prev.events + 100000, 10000000),
            accuracy: Math.min(prev.accuracy + 2.5, 99.7),
            response: Math.min(prev.response + 2, 87),
          }));
        }, 50);
        setTimeout(() => clearInterval(interval), 2000);
      }, 500),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const featureTabs = [
    {
      id: 'ai-intelligence',
      label: 'AI Intelligence',
      icon: <Cpu className="h-5 w-5" />,
      color: 'orange',
    },
    {
      id: 'real-time-monitoring',
      label: 'Real-time Monitoring',
      icon: <Activity className="h-5 w-5" />,
      color: 'blue',
    },
    {
      id: 'compliance-automation',
      label: 'Compliance Automation',
      icon: <Shield className="h-5 w-5" />,
      color: 'purple',
    },
    {
      id: 'enterprise-security',
      label: 'Enterprise Security',
      icon: <Lock className="h-5 w-5" />,
      color: 'green',
    },
  ];

  const aiFeatures = [
    {
      title: 'Predictive Safety Models',
      description:
        'Machine learning algorithms trained on 10M+ construction safety incidents to predict risks with 99.7% accuracy.',
      technical:
        'Advanced neural networks, ensemble learning, real-time inference at 87ms average latency',
      icon: <Target className="h-8 w-8" />,
    },
    {
      title: 'Automated Risk Scoring',
      description:
        'Real-time risk assessment across workers, equipment, weather, and site conditions with continuous model updates.',
      technical:
        'Multi-factor risk matrices, dynamic scoring algorithms, confidence intervals and uncertainty quantification',
      icon: <BarChart3 className="h-8 w-8" />,
    },
    {
      title: 'Intelligent Alerts',
      description:
        'Context-aware notifications that prioritize critical safety events and reduce alert fatigue.',
      technical:
        'Natural language processing, priority clustering, smart notification routing and escalation',
      icon: <Bell className="h-8 w-8" />,
    },
    {
      title: 'Pattern Recognition',
      description:
        'AI identifies hidden correlations between weather, crew fatigue, equipment status, and incident probability.',
      technical:
        'Deep learning pattern detection, correlation analysis, temporal pattern mining and anomaly detection',
      icon: <Sparkles className="h-8 w-8" />,
    },
  ];

  const monitoringFeatures = [
    {
      title: 'Real-time Worker Tracking',
      description:
        'GPS-enabled location monitoring, check-in systems, and automated attendance with privacy controls.',
      technical:
        'GPS geofencing, beacon technology, RFID integration, privacy-compliant tracking protocols',
      icon: <Users className="h-8 w-8" />,
    },
    {
      title: 'Environmental Monitoring',
      description:
        'Weather integration, air quality sensors, noise monitoring, and hazard detection systems.',
      technical:
        'API integrations with NOAA, custom IoT sensors, real-time data fusion and environmental correlation',
      icon: <Globe className="h-8 w-8" />,
    },
    {
      title: 'Equipment Status Tracking',
      description:
        'Monitor equipment health, maintenance schedules, safety inspections, and operational status.',
      technical:
        'IoT sensor integration, predictive maintenance algorithms, equipment lifecycle management',
      icon: <Settings className="h-8 w-8" />,
    },
    {
      title: 'Mobile-First Design',
      description:
        'Native mobile apps for iOS and Android with offline capability and real-time synchronization.',
      technical:
        'React Native, offline-first architecture, background sync, push notifications, biometric auth',
      icon: <Smartphone className="h-8 w-8" />,
    },
  ];

  const complianceFeatures = [
    {
      title: 'OSHA Compliance Automation',
      description:
        'Automated OSHA 300 logging, incident reporting, and regulatory submission with audit trails.',
      technical:
        'OSHA API integration, automated form generation, digital signatures, compliance workflow engines',
      icon: <FileText className="h-8 w-8" />,
    },
    {
      title: 'Digital Certifications',
      description:
        'Track worker certifications, training records, and qualification verification with automated renewals.',
      technical:
        'Certificate lifecycle management, expiration monitoring, training integration APIs, verification systems',
      icon: <CheckCircle className="h-8 w-8" />,
    },
    {
      title: 'Audit Preparation',
      description:
        'Automated audit-ready documentation, compliance reporting, and regulatory change management.',
      technical:
        'Document management systems, regulatory tracking, automated compliance scoring and gap analysis',
      icon: <Database className="h-8 w-8" />,
    },
    {
      title: 'Multi-Jurisdiction Support',
      description:
        'Compliance management across different states, countries, and regulatory environments.',
      technical:
        'Multi-tenant compliance engines, jurisdiction-specific rule sets, automated regulatory updates',
      icon: <Globe className="h-8 w-8" />,
    },
  ];

  const securityFeatures = [
    {
      title: 'SOC 2 Type II Compliance',
      description:
        'Annual SOC 2 audits, comprehensive security controls, and continuous monitoring of security posture.',
      technical:
        'Security control frameworks, vulnerability management, incident response procedures, audit logging',
      icon: <Shield className="h-8 w-8" />,
    },
    {
      title: 'Enterprise SSO Integration',
      description:
        'SAML 2.0, OAuth 2.0, and LDAP integration with major identity providers like Okta, Azure AD, Google.',
      technical:
        'SAML 2.0/OAuth 2.0 protocols, SCIM provisioning, role-based access control, session management',
      icon: <Lock className="h-8 w-8" />,
    },
    {
      title: 'Data Encryption',
      description:
        'End-to-end encryption in transit and at rest with enterprise-grade key management.',
      technical:
        'AES-256 encryption, TLS 1.3, HSM key storage, encrypted database fields, secure backup systems',
      icon: <Database className="h-8 w-8" />,
    },
    {
      title: 'Enterprise Infrastructure',
      description:
        '99.99% uptime SLA, auto-scaling, disaster recovery, and enterprise support with dedicated success managers.',
      technical:
        'Multi-region deployment, automated failover, backup strategies, 24/7 monitoring and alerting',
      icon: <Cloud className="h-8 w-8" />,
    },
  ];

  const getCurrentFeatures = () => {
    switch (activeTab) {
      case 'ai-intelligence':
        return aiFeatures;
      case 'real-time-monitoring':
        return monitoringFeatures;
      case 'compliance-automation':
        return complianceFeatures;
      case 'enterprise-security':
        return securityFeatures;
      default:
        return aiFeatures;
    }
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Global Space Grotesk font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
        * {
          font-family: "Space Grotesk", system-ui, sans-serif !important;
        }
      `}</style>

      {/* Header */}
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled ? 'bg-gray-900/95 backdrop-blur-xl border-b border-gray-800' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/">
              <VHLogo />
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/features" className="text-orange-400 font-medium">
                Features
              </Link>
              <Link
                to="/pricing"
                className="text-gray-300 hover:text-white font-medium transition-colors"
              >
                Pricing
              </Link>
              <Link
                to="/contact"
                className="text-gray-300 hover:text-white font-medium transition-colors"
              >
                Contact
              </Link>

              <div className="flex items-center space-x-4 ml-8 pl-8 border-l border-gray-700">
                <Link
                  to="/login"
                  className="text-gray-300 hover:text-white font-medium transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-500/25"
                >
                  Get Started
                </Link>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section with Floating Elements */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            {/* Floating particles */}
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-orange-500/60 rounded-full animate-pulse"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${2 + Math.random() * 3}s`,
                }}
              />
            ))}

            {/* Animated grid */}
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `
                     linear-gradient(rgba(249, 115, 22, 0.1) 1px, transparent 1px),
                     linear-gradient(90deg, rgba(249, 115, 22, 0.1) 1px, transparent 1px)
                   `,
                backgroundSize: '60px 60px',
                animation: 'gridMove 25s linear infinite',
              }}
            ></div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center relative">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 animate-fade-in">
            Enterprise Construction
            <br />
            <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
              Safety Intelligence
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed animate-fade-in-delay">
            Advanced AI capabilities, real-time monitoring, automated compliance, and enterprise
            security designed specifically for construction operations at scale.
          </p>

          {/* Live Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 animate-fade-in-delay-2">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 hover:border-orange-500/50 transition-all duration-500 group hover:scale-105">
              <div className="text-3xl font-bold text-orange-400 mb-2">
                {animatedCounts.events.toLocaleString()}+
              </div>
              <div className="text-gray-400">Safety Events Analyzed</div>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 hover:border-blue-500/50 transition-all duration-500 group hover:scale-105">
              <div className="text-3xl font-bold text-blue-400 mb-2">
                {animatedCounts.accuracy.toFixed(1)}%
              </div>
              <div className="text-gray-400">AI Prediction Accuracy</div>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800 hover:border-purple-500/50 transition-all duration-500 group hover:scale-105">
              <div className="text-3xl font-bold text-purple-400 mb-2">
                {animatedCounts.response}ms
              </div>
              <div className="text-gray-400">Average Response Time</div>
            </div>
          </div>

          {/* Feature Navigation Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {featureTabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-500 transform hover:scale-105 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25 scale-105'
                    : 'bg-gray-900/50 text-gray-300 hover:bg-gray-800 border border-gray-800 hover:border-orange-500/30'
                }`}
                style={{
                  animationDelay: `${index * 0.1}s`,
                }}
              >
                <div
                  className={`${activeTab === tab.id ? 'animate-spin' : ''}`}
                  style={{ animationDuration: '3s' }}
                >
                  {tab.icon}
                </div>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Custom CSS for animations */}
        <style>{`
          @keyframes gridMove {
            0% { transform: translateX(0) translateY(0); }
            100% { transform: translateX(60px) translateY(60px); }
          }
          @keyframes fade-in {
            0% { opacity: 0; transform: translateY(30px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in {
            animation: fade-in 1s ease-out;
          }
          .animate-fade-in-delay {
            animation: fade-in 1s ease-out 0.3s both;
          }
          .animate-fade-in-delay-2 {
            animation: fade-in 1s ease-out 0.6s both;
          }
        `}</style>
      </section>

      {/* Interactive Feature Details Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background animation */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute w-64 h-64 bg-gradient-to-br from-orange-500/20 to-purple-500/20 rounded-full blur-3xl animate-bounce"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${i * 2}s`,
                animationDuration: `${8 + i * 2}s`,
              }}
            />
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {getCurrentFeatures().map((feature, index) => (
              <div
                key={index}
                className="group relative bg-gray-900/40 backdrop-blur-sm rounded-2xl p-8 border border-gray-800 hover:border-orange-500/50 transition-all duration-700 hover:scale-105 cursor-pointer"
                style={{
                  animation: `slideInUp 0.8s ease-out ${index * 0.2}s both`,
                }}
              >
                {/* Animated glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-2xl"></div>

                {/* Floating icon container */}
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-125 group-hover:rotate-6 transition-all duration-500 text-white shadow-lg shadow-orange-500/30 relative overflow-hidden">
                    {/* Icon with glow effect */}
                    <div className="relative z-10">{feature.icon}</div>
                    {/* Rotating background glow */}
                    <div className="absolute inset-0 bg-gradient-to-r from-orange-400/50 to-orange-600/50 animate-pulse rounded-2xl"></div>
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-orange-400 transition-colors duration-500 group-hover:translate-x-2">
                    {feature.title}
                  </h3>

                  <p className="text-gray-400 mb-6 leading-relaxed text-lg group-hover:text-gray-300 transition-colors duration-500">
                    {feature.description}
                  </p>

                  {/* Interactive technical specs */}
                  <div className="bg-gray-800/30 rounded-lg p-4 opacity-80 group-hover:opacity-100 transition-all duration-500 group-hover:bg-gray-800/50 transform group-hover:scale-105">
                    <div className="text-orange-400 font-medium text-sm mb-2 flex items-center group-hover:text-orange-300 transition-colors">
                      <Cpu className="h-4 w-4 mr-2 animate-pulse" />
                      Technical Specifications
                    </div>
                    <div className="text-xs text-gray-300 leading-relaxed group-hover:text-gray-200 transition-colors">
                      {feature.technical}
                    </div>
                  </div>

                  {/* Live indicator */}
                  <div className="absolute top-4 right-4 flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
                    <span className="text-green-400 text-xs font-medium">ACTIVE</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional CSS for smooth animations */}
        <style>{`
          @keyframes slideInUp {
            0% { 
              opacity: 0; 
              transform: translateY(40px) scale(0.95);
            }
            100% { 
              opacity: 1; 
              transform: translateY(0) scale(1);
            }
          }
        `}</style>
      </section>

      {/* Enterprise Integrations */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-orange-500/5 to-transparent"></div>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Enterprise Integrations
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Seamlessly connect with your existing construction technology stack and enterprise
              systems.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Project Management */}
            <div className="bg-gray-900/40 backdrop-blur-sm rounded-2xl p-8 border border-gray-800">
              <h3 className="text-xl font-bold text-white mb-6">Project Management</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-300">Procore</span>
                  <span className="text-green-400 text-sm">Native Integration</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-300">Autodesk Construction Cloud</span>
                  <span className="text-green-400 text-sm">API Integration</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-300">Oracle Primavera</span>
                  <span className="text-green-400 text-sm">API Integration</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-300">Microsoft Project</span>
                  <span className="text-blue-400 text-sm">Webhook Sync</span>
                </div>
              </div>
            </div>

            {/* Enterprise Systems */}
            <div className="bg-gray-900/40 backdrop-blur-sm rounded-2xl p-8 border border-gray-800">
              <h3 className="text-xl font-bold text-white mb-6">Enterprise Systems</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-300">SAP ERP</span>
                  <span className="text-green-400 text-sm">Native Integration</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-300">Oracle ERP</span>
                  <span className="text-green-400 text-sm">API Integration</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-300">ADP Workforce</span>
                  <span className="text-green-400 text-sm">Native Integration</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-300">Workday HCM</span>
                  <span className="text-blue-400 text-sm">API Integration</span>
                </div>
              </div>
            </div>

            {/* Safety & Compliance */}
            <div className="bg-gray-900/40 backdrop-blur-sm rounded-2xl p-8 border border-gray-800">
              <h3 className="text-xl font-bold text-white mb-6">Safety & Compliance</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-300">ISN Database</span>
                  <span className="text-green-400 text-sm">Native Integration</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-300">BROWZ</span>
                  <span className="text-green-400 text-sm">API Integration</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-300">PEC Safety</span>
                  <span className="text-blue-400 text-sm">Data Export</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-gray-300">OSHA Database</span>
                  <span className="text-green-400 text-sm">API Integration</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-32 relative">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Technical Architecture
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Enterprise-grade infrastructure built for scale, security, and performance.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Performance Specs */}
            <div className="space-y-8">
              <h3 className="text-2xl font-bold text-white mb-6">Performance & Scale</h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Processing Speed</h4>
                    <div className="text-gray-400">87ms average AI response time</div>
                    <div className="text-gray-400">2.4M+ safety events processed per minute</div>
                    <div className="text-gray-400">Real-time data synchronization</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Layers className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Scalability</h4>
                    <div className="text-gray-400">Unlimited construction sites</div>
                    <div className="text-gray-400">Unlimited worker accounts</div>
                    <div className="text-gray-400">Auto-scaling infrastructure</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Reliability</h4>
                    <div className="text-gray-400">99.99% uptime SLA</div>
                    <div className="text-gray-400">Multi-region deployment</div>
                    <div className="text-gray-400">Automated disaster recovery</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Specs */}
            <div className="space-y-8">
              <h3 className="text-2xl font-bold text-white mb-6">Security & Compliance</h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">
                      Security Certifications
                    </h4>
                    <div className="text-gray-400">SOC 2 Type II compliant</div>
                    <div className="text-gray-400">GDPR and CCPA compliant</div>
                    <div className="text-gray-400">ISO 27001 certified infrastructure</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Lock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Data Protection</h4>
                    <div className="text-gray-400">AES-256 encryption at rest</div>
                    <div className="text-gray-400">TLS 1.3 encryption in transit</div>
                    <div className="text-gray-400">Zero-knowledge architecture</div>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Access Control</h4>
                    <div className="text-gray-400">Enterprise SSO (SAML 2.0, OAuth)</div>
                    <div className="text-gray-400">Role-based permissions</div>
                    <div className="text-gray-400">Multi-factor authentication</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Live Dashboard Preview */}
      <section className="py-32 relative overflow-hidden">
        {/* Animated background particles */}
        <div className="absolute inset-0 opacity-30">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-orange-400/40 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${3 + Math.random() * 2}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </div>

        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              See the Platform in Action
            </h2>
            <p className="text-xl text-gray-400">
              Real dashboard components from the Virtual Hardhat platform.
            </p>
          </div>

          {/* Enhanced Dashboard Preview */}
          <div className="relative group hover:scale-105 transition-transform duration-700">
            {/* Animated glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-purple-500/20 blur-3xl animate-pulse"></div>

            {/* Floating elements around dashboard */}
            <div
              className="absolute -top-8 -left-8 w-16 h-16 bg-orange-500/20 rounded-full animate-bounce"
              style={{ animationDuration: '3s' }}
            ></div>
            <div
              className="absolute -top-4 -right-4 w-12 h-12 bg-blue-500/20 rounded-full animate-bounce"
              style={{ animationDuration: '4s', animationDelay: '1s' }}
            ></div>
            <div
              className="absolute -bottom-6 -left-6 w-10 h-10 bg-purple-500/20 rounded-full animate-bounce"
              style={{ animationDuration: '3.5s', animationDelay: '2s' }}
            ></div>

            <div className="relative bg-white rounded-2xl border border-gray-200 p-6 shadow-2xl overflow-hidden">
              {/* Animated top bar */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <div
                    className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"
                    style={{ animationDelay: '0.5s' }}
                  ></div>
                  <div
                    className="w-3 h-3 bg-green-500 rounded-full animate-pulse"
                    style={{ animationDelay: '1s' }}
                  ></div>
                  <span className="text-gray-500 text-sm ml-4 font-mono">
                    dashboard.virtual-hardhat.com
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
                  <span className="text-green-600 text-sm font-medium animate-pulse">
                    LIVE PLATFORM
                  </span>
                </div>
              </div>

              {/* Real VH Dashboard Components with animations */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="transform hover:scale-110 hover:rotate-2 transition-all duration-500 hover:z-10 relative">
                  <ComplianceScoreWidget data={{ compliancePct: 87 }} />
                </div>

                <div className="transform hover:scale-110 hover:-rotate-1 transition-all duration-500 hover:z-10 relative delay-100">
                  <ActiveWorkersWidget
                    data={{
                      workers: Array(45)
                        .fill({})
                        .map((_, i) => ({ id: i, active: i < 38 })),
                    }}
                  />
                </div>

                <div className="transform hover:scale-110 hover:rotate-1 transition-all duration-500 hover:z-10 relative delay-200">
                  <OpenHazardsWidget />
                </div>
              </div>

              {/* Floating data indicators */}
              <div className="absolute top-20 right-20 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="bg-black/80 text-white px-3 py-2 rounded-lg text-xs font-mono">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span>Live Data Stream</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(180deg); }
          }
        `}</style>
      </section>

      {/* Call to Action */}
      <section className="py-40 relative">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-8">Ready to Deploy?</h2>
          <p className="text-xl text-gray-400 mb-16 max-w-2xl mx-auto">
            Experience enterprise-grade construction safety intelligence. Start your free trial
            today.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/signup"
              className="group inline-flex items-center justify-center px-12 py-5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-semibold text-xl hover:from-orange-600 hover:to-orange-700 transition-all hover:scale-105 shadow-xl shadow-orange-500/25"
            >
              Start Free Trial
              <ArrowRight className="ml-3 h-6 w-6 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center px-12 py-5 bg-white/5 backdrop-blur-sm border border-white/10 text-white rounded-xl font-semibold text-xl hover:bg-white/10 transition-all hover:scale-105"
            >
              Schedule Enterprise Demo
              <ArrowRight className="ml-3 h-6 w-6 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="md:col-span-1">
              <VHLogo />
              <p className="text-gray-500 mt-4 text-sm">Enterprise AI for construction safety.</p>
            </div>

            <div>
              <h4 className="text-white font-medium mb-4">Platform</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/features" className="text-gray-400 hover:text-white transition-colors">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-gray-400 hover:text-white transition-colors">
                    Security
                  </Link>
                </li>
                <li>
                  <Link
                    to="/integrations"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Integrations
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-medium mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/careers" className="text-gray-400 hover:text-white transition-colors">
                    Careers
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-medium mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                    Terms
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-gray-400 hover:text-white transition-colors">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Virtual Hardhat, Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
